import type { ChartPermissionOutput } from "@compass/shared/schema/chart";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Button } from "components/elements";
import { trpc } from "lib/trpc";
import { ChartPermissionPicker, type PermissionEntry } from "./ChartPermissionPicker";
import { useExternalOrgConfirm } from "./useExternalOrgConfirm";

interface PermissionsModalProps {
    isOpen: boolean;
    onClose: () => void;
    chartId: number;
    permissions: ChartPermissionOutput[];
    onUpdated: () => void;
}

function toEntries(permissions: ChartPermissionOutput[]): PermissionEntry[] {
    return permissions.map((p) => ({
        target: p.target as PermissionEntry["target"],
        targetId:
            p.target === "USER" ? p.userId! : p.target === "ORGANIZATION" ? p.organizationId! : 0,
        label:
            p.target === "COMPASS_TEAM"
                ? "Compass Team"
                : p.target === "PUBLIC"
                  ? "Public"
                  : p.target === "USER"
                    ? p.userName || `User #${p.userId}`
                    : p.organizationName || `Org #${p.organizationId}`,
        role: p.role,
        orgLabel: p.target === "USER" ? (p.userOrganizationName ?? undefined) : undefined,
        orgId:
            p.target === "ORGANIZATION"
                ? (p.organizationId ?? undefined)
                : p.target === "USER"
                  ? (p.userOrganizationId ?? undefined)
                  : undefined,
    }));
}

export const PermissionsModal = ({
    isOpen,
    onClose,
    chartId,
    permissions,
    onUpdated,
}: PermissionsModalProps) => {
    const [entries, setEntries] = useState<PermissionEntry[]>(() => toEntries(permissions));
    const [isSaving, setIsSaving] = useState(false);
    const { onBeforeAdd: handleBeforeAdd, modal: externalOrgConfirmModal } = useExternalOrgConfirm({
        existingEntries: entries,
    });

    const addMutation = trpc.charts.addPermission.useMutation();
    const removeMutation = trpc.charts.removePermission.useMutation();
    const updateMutation = trpc.charts.updatePermission.useMutation();

    // Sync entries when permissions prop changes (e.g. after refetch)
    useEffect(() => {
        if (isOpen) {
            setEntries(toEntries(permissions));
        }
    }, [permissions, isOpen]);

    if (!isOpen) return null;

    const handleSave = async () => {
        setIsSaving(true);
        try {
            const original = permissions;
            const originalKeys = new Map(
                original.map((p) => {
                    const key =
                        p.target === "USER"
                            ? `USER:${p.userId}`
                            : p.target === "ORGANIZATION"
                              ? `ORGANIZATION:${p.organizationId}`
                              : p.target === "COMPASS_TEAM"
                                ? "COMPASS_TEAM:0"
                                : `PUBLIC:0`;
                    return [key, p];
                }),
            );

            const newKeys = new Map(entries.map((e) => [`${e.target}:${e.targetId}`, e]));

            // Remove permissions that are no longer in the list
            for (const [key, perm] of originalKeys) {
                if (!newKeys.has(key)) {
                    await removeMutation.mutateAsync({ chartId, permissionId: perm.id });
                }
            }

            // Add new permissions and update changed roles
            for (const [key, entry] of newKeys) {
                const existing = originalKeys.get(key);
                if (!existing) {
                    await addMutation.mutateAsync({
                        chartId,
                        role: entry.role,
                        target: entry.target as "USER" | "ORGANIZATION" | "PUBLIC" | "COMPASS_TEAM",
                        userId: entry.target === "USER" ? entry.targetId : undefined,
                        organizationId:
                            entry.target === "ORGANIZATION" ? entry.targetId : undefined,
                    });
                } else if (existing.role !== entry.role) {
                    await updateMutation.mutateAsync({
                        permissionId: existing.id,
                        role: entry.role,
                    });
                }
            }

            enqueueSnackbar("Permissions updated", { variant: "success" });
            onUpdated();
            onClose();
        } catch {
            enqueueSnackbar("Failed to update permissions", { variant: "error" });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                onClick={onClose}
            >
                <div
                    className="max-h-[80vh] w-[600px] max-w-[90vw] overflow-auto rounded-xl bg-white p-6 shadow-xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    <h2 className="mb-4 text-lg font-semibold">Manage Permissions</h2>

                    <ChartPermissionPicker
                        value={entries}
                        onChange={setEntries}
                        onBeforeAdd={handleBeforeAdd}
                    />

                    <div className="mt-6 flex justify-end gap-3">
                        <Button variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button onClick={handleSave} disabled={isSaving}>
                            {isSaving ? "Saving..." : "Save Changes"}
                        </Button>
                    </div>
                </div>
            </div>

            {externalOrgConfirmModal}
        </>
    );
};
